import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { emptyArray } from 'ui-components/default-props/empty';
import { PagingContext } from '../../../../contexts/paging-context';
import { entitiesLoad } from '../../../../store/entities/entities.actions';
import { createLookupHash } from '../../../../services/hash-service';
import { getReportingCategories } from '../../../../store/products/products.api';
import { descending } from '../../../../constants/sortDirections';

const ReportingCategoryContext = createContext({});

const ReportingCategoryContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [reportingCategoryIds, setReportingCategoryIds] = useAsyncSafeState([]);
  const [isLoading, setIsLoading] = useAsyncSafeState(true);
  const { page, pageSize } = useContext(PagingContext);
  const [orderBy, setOrderBy] = useAsyncSafeState('name');
  const [direction, setDirection] = useAsyncSafeState(descending);
  const [totalRecords, setTotalRecords] = useAsyncSafeState(0);
  const [needsReload, setNeedsReload] = useAsyncSafeState(false);

  useEffect(() => {
    const reportingCategoryPromise = getReportingCategories(page, pageSize, orderBy, direction);
    reportingCategoryPromise.promise
      .then((response) => {
        const categories = get(response, 'data.categories', emptyArray);
        const ids = categories.map((cat) => cat.categoryId);
        setReportingCategoryIds(ids);
        const totalRecordCount = get(response, 'data.totalCount', 0);
        setTotalRecords(totalRecordCount);
        setIsLoading(false);
        const entitiesToLoad = {
          entities: {
            reportingCategories: createLookupHash(categories, 'categoryId'),
          },
        };
        dispatch(entitiesLoad(entitiesToLoad));
      })
      .catch(() => {
        setIsLoading(false);
        dispatch(
          entitiesLoad({
            entities: {
              reportingCategories: {},
            },
          }),
        );
      });
    return () => reportingCategoryPromise?.cancel();
  }, [page, pageSize, orderBy, direction, dispatch, needsReload, setReportingCategoryIds, setIsLoading, setTotalRecords]);

  const value = useMemo(
    () => ({
      reportingCategoryIds,
      isLoading,
      totalRecords,
      orderBy,
      setOrderBy,
      direction,
      setDirection,
      needsReload,
      setNeedsReload,
    }),
    [reportingCategoryIds, isLoading, totalRecords, orderBy, setOrderBy, direction, setDirection, needsReload, setNeedsReload],
  );

  return <ReportingCategoryContext.Provider value={value}>{children}</ReportingCategoryContext.Provider>;
};

ReportingCategoryContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export { ReportingCategoryContext };

export default ReportingCategoryContextProvider;
